import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";

export default function Terms() {
  useSEO({
    title: "Terms of Service — Maqam",
    description: "Terms governing your use of Maqam, including the Gmail Send integration for sending job applications on your behalf.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white" data-testid="terms-page">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-display font-black text-xl tracking-tighter inline-flex items-center">
              <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span>
              <span>aqam</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white">← Back home</Link>
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 md:px-10 py-16 space-y-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-3">/ Legal</div>
          <h1 className="font-display font-normal text-4xl md:text-5xl tracking-tight">Terms of Service</h1>
          <p className="text-white/50 text-sm mt-3">Last updated: 25 April 2026 · Governed by the laws of the Kingdom of Saudi Arabia</p>
        </div>

        <section className="space-y-4 text-white/80 leading-relaxed">
          <p>
            By creating an account on Maqam (<span className="font-mono">maqam-ksa.com</span>) you agree to
            these Terms. If you do not agree, do not use the service.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">1. The service</h2>
          <p className="text-white/75">
            Maqam is an AI-powered job-application platform aggregating publicly listed roles in
            Saudi Arabia and the Gulf. We help you discover jobs, generate AI-tailored cover letters,
            and bulk-send applications to employers — either via your connected Gmail account or via
            our outbound infrastructure (Resend), with your explicit per-session consent.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">2. User obligations</h2>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li>Provide accurate personal and CV information. False information may result in account suspension.</li>
            <li>Use the platform only for legitimate job-search purposes. Spam, fraud, recruitment-scraping, or impersonation is forbidden.</li>
            <li>Respect daily/monthly quotas tied to your subscription tier (Free / Basic / Pro / Agency).</li>
            <li>Do not attempt to reverse-engineer, scrape, or replicate Maqam's source code, AI prompts, or job-database compilation.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">3. Subscriptions & billing</h2>
          <p className="text-white/75">
            Paid tiers are billed monthly in SAR via Stripe (international cards) or Moyasar (mada, Saudi cards).
            Cancellation takes effect at the end of the billing period. No refunds for partial months,
            except as required by Saudi consumer-protection law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">4. Intellectual property</h2>
          <p className="text-white/75">
            All source code, branding, AI prompt engineering, database compilations, and UX flows are
            © 2026 Maqam, registered (or pending registration) with the Saudi Authority for Intellectual
            Property (SAIP). You retain full ownership of your CV content, cover letters, and all data
            you upload — Maqam holds only a limited license to process them on your behalf to provide
            the service.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">5. Disclaimers</h2>
          <ul className="list-disc ps-6 space-y-2 text-white/75">
            <li>Maqam does not guarantee any job offer, interview, or employer response.</li>
            <li>AI-generated cover letters are produced by Claude Sonnet 4.5; you remain solely responsible for their content before submission.</li>
            <li>Job listings are pulled from third-party sources (RapidAPI JSearch, LinkedIn). We are not responsible for inaccurate, expired, or fraudulent postings.</li>
            <li>The service is provided "as is" without warranties beyond what is required by Saudi law.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">6. Termination</h2>
          <p className="text-white/75">
            You may delete your account at any time from <Link to="/app/privacy" className="link-underline text-white">your privacy dashboard</Link>.
            Maqam may suspend or terminate accounts violating these Terms after written notice (or
            immediately for fraud, abuse, or legal compulsion).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">7. Governing law & jurisdiction</h2>
          <p className="text-white/75">
            These Terms are governed by the laws of the Kingdom of Saudi Arabia. Any dispute shall be
            settled exclusively before the competent commercial courts of Riyadh.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-bold text-2xl">8. Contact</h2>
          <p className="text-white/75">
            Legal: <a href="mailto:legal@maqam-ksa.com" className="link-underline text-white">legal@maqam-ksa.com</a><br/>
            Support: <a href="mailto:support@maqam-ksa.com" className="link-underline text-white">support@maqam-ksa.com</a>
          </p>
        </section>

        <footer className="border-t border-white/10 pt-8 text-xs text-white/40">
          © 2026 Maqam. All rights reserved. — Maqam™ is a registered trademark in the Kingdom of Saudi Arabia.
        </footer>
      </main>
    </div>
  );
}
